import { listCases } from '@/backend/case'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { type Case } from './types'

/**
 * This store holds all state relating to cases, including:
 * - All cases in the current workspace
 * - The currently active case
 */
export const useCaseStore = defineStore('case', () => {
  /**
   * Holds the total number of cases in the workspace, as fetched from the backend.
   * 0 means not yet loaded or no cases.
   */
  const caseCount = ref<number>(0)
  /**
   * Tracks the loading state of the caseCount fetch operation.
   */
  const caseCountLoading = ref(false)

  const activeCase = ref<Case | null>(null)
  const activeCaseId = computed(() => activeCase.value?.id || null)

  const setActiveCase = async (newCase: Case) => {
    activeCase.value = newCase
  }

  const $reset = (): void => {
    activeCase.value = null
  }

  /**
   * Updates the caseCount and caseCountLoading refs by fetching the case count directly from the backend.
   * This is the only place that updates these refs.
   * @param workspaceId string - the workspace to fetch the case count for
   */
  async function updateCaseCount(workspaceId: string, searchValue: string) {
    caseCountLoading.value = true
    try {
      const res = await listCases({ workspaceId, offset: 1, limit: 1, name_contains: searchValue })
      if (res.ok) {
        caseCount.value = res.data.metadata.total_count ?? 0
      }
    } catch {
      caseCount.value = 0
    }
    caseCountLoading.value = false
  }

  return {
    activeCase,
    activeCaseId,
    setActiveCase,

    $reset,
    caseCount,
    caseCountLoading,
    updateCaseCount,
  }
})
